'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'

interface Capability {
  icon: React.ElementType
  label: string
  query: string
}

// ── Default capabilities (no entity context) ──────────────────────────────
const DEFAULT_CAPABILITIES: Capability[] = [
  { icon: Icons.helpCircle,   label: 'What can you help me with?', query: 'What can you help me with?' },
  { icon: Icons.activity,     label: 'Dashboard KPIs',             query: "Show me today's O2C dashboard stats" },
  { icon: Icons.alertTriangle,label: 'Show exceptions',            query: "Show me today's exception orders" },
  { icon: Icons.brain,        label: 'Create a policy',            query: 'Help me create a new O2C policy' },
  { icon: Icons.dollarSign,   label: 'Cash at risk',               query: "What's our total cash at risk?" },
  { icon: Icons.info,         label: 'Explain this page',          query: 'Explain this page to me' },
]

// ── Order detail page capabilities (matches spec §5.6 exactly) ────────────
const ORDER_CAPABILITIES: Capability[] = [
  { icon: Icons.alertTriangle, label: 'Why was this order flagged?',             query: 'Why was this order flagged?' },
  { icon: Icons.dollarSign,    label: "What's my recovery chance on the draft?", query: "What's my recovery chance on the draft deduction?" },
  { icon: Icons.search,        label: 'Has this customer done this before?',     query: 'Has this customer done this before?' },
  { icon: Icons.mail,          label: 'Draft a dispute email for me',            query: 'Draft a dispute email for me' },
  { icon: Icons.brain,         label: 'Which policies fired on this order?',     query: 'Which policies fired on this order?' },
]

// ── Deduction detail page capabilities (matches spec §5.6 exactly) ────────
const DEDUCTION_CAPABILITIES: Capability[] = [
  { icon: Icons.helpCircle, label: 'Should I dispute or accept this?',       query: 'Should I dispute or accept this deduction?' },
  { icon: Icons.fileText,   label: 'What evidence do I need to dispute?',    query: 'What evidence do I need to dispute?' },
  { icon: Icons.clock,      label: 'How long will this take to resolve?',    query: 'How long will this take to resolve?' },
  { icon: Icons.search,     label: 'Has this retailer done this before?',    query: 'Has this retailer filed similar deductions?' },
]

interface CapabilityBubblesProps {
  onSelect: (query: string) => void
  entityType?: 'order' | 'deduction' | null
  entityId?: string | null
  hasDiscrepancies?: boolean
  hasDraftDeductions?: boolean
}

export function CapabilityBubbles({
  onSelect,
  entityType,
  hasDiscrepancies,
  hasDraftDeductions,
}: CapabilityBubblesProps) {
  // Order capabilities: optionally surface contextual extras when we know
  // there are discrepancies or draft deductions on this specific order
  let orderCaps = [...ORDER_CAPABILITIES]
  if (entityType === 'order') {
    if (hasDiscrepancies) {
      orderCaps = [
        { icon: Icons.alertTriangle, label: 'Why was this order flagged?', query: 'Why was this order flagged?' },
        { icon: Icons.fileText, label: 'Explain the price discrepancies', query: 'Explain the price discrepancies on this order' },
        { icon: Icons.dollarSign, label: "What's my recovery chance?", query: "What's my recovery chance on the draft deduction?" },
        { icon: Icons.mail, label: 'Draft a dispute email for me', query: 'Draft a dispute email for me' },
        { icon: Icons.brain, label: 'Which policies fired on this order?', query: 'Which policies fired on this order?' },
      ]
    }
    if (hasDraftDeductions && !hasDiscrepancies) {
      orderCaps = [
        { icon: Icons.alertTriangle, label: 'Why was this order flagged?', query: 'Why was this order flagged?' },
        { icon: Icons.checkCircle, label: "Recovery chance on draft deduction?", query: "What's my recovery chance on the draft deduction?" },
        { icon: Icons.search, label: 'Has this customer done this before?', query: 'Has this customer done this before?' },
        { icon: Icons.mail, label: 'Draft a dispute email for me', query: 'Draft a dispute email for me' },
        { icon: Icons.brain, label: 'Which policies fired on this order?', query: 'Which policies fired on this order?' },
      ]
    }
  }

  const capabilities =
    entityType === 'order'     ? orderCaps :
    entityType === 'deduction' ? DEDUCTION_CAPABILITIES :
    DEFAULT_CAPABILITIES

  return (
    <div className="flex flex-wrap justify-center gap-2 p-2">
      {capabilities.map((cap, i) => {
        const Icon = cap.icon
        return (
          <motion.button
            key={`${entityType ?? 'default'}-${i}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.25, ease: 'easeOut' }}
            onClick={() => onSelect(cap.query)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-full',
              'bg-white border border-brand-cornflower/20',
              'text-sm text-brand-navy',
              'shadow-sm',
              'transition-all duration-200',
              'hover:bg-brand-cornflower/10 hover:border-brand-cornflower/40 hover:shadow-md',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
            )}
          >
            <Icon className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
            <span className="text-xs sm:text-sm font-medium">{cap.label}</span>
          </motion.button>
        )
      })}
    </div>
  )
}
